<script lang="ts" setup>

import {getLastMessageDate} from "@src/helpers/lastMessageHelper";
import {resolveUrl, shorten} from "@src/helpers/utils";
import {computed} from "vue";
import useStore from "@src/stores/store";
import {useChatSessionStore} from "@src/stores/chatSession";

const props = defineProps([
    'id',
    'name',
    'type',
    'lastMessage',
    'lastMessageDate',
    'avatar',
    'department',
    'hasNewMessage',
    'isExpired',
    'session'
]);

const emit = defineEmits([
    'send-message',
    'schedule-message',
    'send-campaign'
]);

const store = useStore()
const sessionStore = useChatSessionStore()

const lastMessageDate = computed(() => {
    if (props.lastMessageDate) {
        return getLastMessageDate(props.lastMessageDate);
    }

    return '';
});

const getIconUrl = (iconName: string) => {
    return new URL(`/src/assets/vectors/logo_${iconName}.svg`, import.meta.url).href;
}

const sessionClass = computed(() => {
    let base = 'flex items-start px-3 py-3 cursor-pointer my-3 rounded-md transition-colors w-full min-w-0 shadow-sm ';

    if (props.isExpired) {
        return base + 'bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:hover:bg-red-900/70';
    }

    if (props.hasNewMessage) {
        return base + 'bg-green-100 hover:bg-green-200 dark:bg-[#144d37] dark:hover:bg-[#195e42]';
    }

    if (sessionStore.selectedSession && sessionStore.selectedSession.id === props.id) {
        return base + 'bg-gray-100 dark:bg-gray-700';
    }

    return base + 'bg-white hover:bg-gray-50';
});

function openSendMessageModal(event: MouseEvent) {
    event.stopPropagation();
    emit('send-message', props.session);
}

function openScheduleMessageModal(event: MouseEvent) {
    event.stopPropagation();
    emit('schedule-message', props.session);
}


function goToSession (){
    store.activeSidebarComponent = 'active'
    sessionStore.selectSession(props.session)
}

function openCampaignModal(event: MouseEvent) {
    event.stopPropagation();
    emit('send-campaign', props.session);
}
</script>

<template>
    <div :class="sessionClass">
        <div class="flex w-full min-w-0">
            <!-- Avatar -->
            <div class="relative flex-shrink-0 h-9">
                <div
                    :style="{ backgroundImage: `url(${resolveUrl(props.avatar)})` }"
                    class="w-8 h-8 rounded-full bg-cover bg-center"
                ></div>

                <img
                    :src="getIconUrl(props.type)"
                    alt="Tipo do Canal"
                    class="absolute bottom-0 left-6.5 z-10 h-5 w-5 rounded-full ring-2 ring-white bg-white"
                />
            </div>

            <!-- Conteúdo -->
            <div class="flex flex-col min-w-0 flex-1 ml-3">
                <!-- Linha superior -->
                <div class="flex items-center justify-between gap-2 min-w-0">
                    <h3 class="text-sm font-semibold text-slate-800 truncate min-w-0">
                        {{ shorten(props.name, 18) }}
                    </h3>

                    <div class="flex items-center gap-2 flex-shrink-0">
                        <span class="text-[10px] text-gray-400 whitespace-nowrap">
                            {{ lastMessageDate }}
                        </span>

                        <span
                            v-if="props.hasNewMessage"
                            class="w-3 h-3 rounded-full bg-green-400 animate-pulse flex-shrink-0"
                        ></span>
                    </div>
                </div>

                <!-- Ações -->
                <div class="flex items-center justify-end gap-2 mt-3 w-full">
                    <!-- Abrir conversa -->
                    <button
                        title="Abrir conversa"
                        class="kanban-action-button bg-[#EEF0FF] text-[#7C8CF5] hover:bg-[#E0E4FF] cursor-pointer"
                        @click.stop="goToSession()"
                    >
                        <svg
                            fill="none"
                            height="17"
                            viewBox="0 0 24 24"
                            width="17"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8.625 12a.375.375 0 1 1-.75 0a.375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0a.375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0a.375.375 0 0 1 .75 0Z"
                                fill="currentColor"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 21.4a.75.75 0 0 1-.645-1.268a4.48 4.48 0 0 0 1.08-1.587C4.09 17.063 3 14.755 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                            />
                        </svg>
                    </button>

                    <!-- Enviar campanha -->
                    <button
                        v-if="props.isExpired && props.session?.channel?.type === 'waapi'"
                        title="Enviar campanha"
                        class="kanban-action-button bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer"
                        @click.stop="openCampaignModal"
                    >
                        <svg
                            fill="none"
                            height="17"
                            viewBox="0 0 24 24"
                            width="17"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                            />
                            <path
                                d="M10.34 15.84a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                            />
                            <path
                                d="M10.34 15.84a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                            />
                            <path
                                d="M19.175 4.125a23.91 23.91 0 0 1 0 14.25"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                            />
                        </svg>

                    </button>

                    <!-- Enviar mensagem -->
                    <button
                        v-else
                        title="Enviar mensagem"
                        class="kanban-action-button bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer"
                        @click.stop="openSendMessageModal"
                    >
                        <svg
                            fill="currentColor"
                            height="16"
                            viewBox="0 -960 960 960"
                            width="16"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M120-160v-640l760 320-760 320Zm80-120 474-200-474-200v140l240 60-240 60v140Zm0 0v-400 400Z"/>
                        </svg>
                    </button>

                    <!-- Mensagens agendadas -->
                    <button
                        v-if="props.session?.channel?.type === 'whatsapp' || props.session?.channel?.type === 'waapi'"
                        title="Mensagens agendadas"
                        class="kanban-action-button bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer"
                        @click.stop="openScheduleMessageModal"
                    >
                        <svg
                            fill="none"
                            height="16"
                            viewBox="0 0 24 24"
                            width="16"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 8v5l3 2"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                            />
                            <path
                                d="M12 21a9 9 0 1 0 0-18a9 9 0 0 0 0 18Z"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.kanban-action-button {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition:
        background-color 160ms ease,
        color 160ms ease,
        transform 160ms ease,
        box-shadow 160ms ease;
}

.kanban-action-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(15, 23, 42, 0.10);
}

.kanban-action-button:active {
    transform: translateY(0);
    box-shadow: none;
}
</style>